/**
 * Utility functions for printing fee structure documents
 */

import moment from "moment";

/**
 * Formats currency amount to Indian Rupee format
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount);
};

/**
 * Calculates total amount from fee structures
 * @param {Array} feeStructures - Array of fee structure objects
 * @returns {number} Total amount
 */
const calculateTotalAmount = (feeStructures) => {
  return feeStructures.reduce(
    (total, structure) => total + Number.parseFloat(structure.amount),
    0
  );
};

/**
 * Generates and prints fee structure document
 * @param {Object} courseInfo - Course information object
 * @param {Array} feeStructures - Array of fee structure objects
 * @param {Object} options - Print options (optional)
 */
export const printFeeStructure = (courseInfo, feeStructures, options = {}) => {
  const {
    schoolName = "iSchool Management System",
    documentTitle = "Fee Structure Details",
    includeFooter = true,
    customStyles = "",
  } = options;

  const printWindow = window.open("", "_blank");
  const currentDate = moment().format("DD-MM-YYYY");
  const totalAmount = calculateTotalAmount(feeStructures);

  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Fee Structure - ${courseInfo.courseName}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 20px;
          color: #333;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #333;
          padding-bottom: 20px;
        }
        .school-name {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        .document-title {
          font-size: 18px;
          font-weight: bold;
          color: #666;
        }
        .course-info {
          background-color: #f8f9fa;
          padding: 15px;
          margin-bottom: 20px;
          border-radius: 5px;
          border: 1px solid #dee2e6;
        }
        .course-info h3 {
          margin-top: 0;
          color: #495057;
        }
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-top: 10px;
        }
        .info-item {
          margin-bottom: 8px;
        }
        .info-label {
          font-weight: bold;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        .amount {
          text-align: right;
        }
        .total-row {
          background-color: #f8f9fa;
          font-weight: bold;
        }
        .footer {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #ddd;
          font-size: 12px;
          color: #666;
        }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
        ${customStyles}
      </style>
    </head>
    <body>
      <div class="header">
        <div class="school-name">${schoolName}</div>
        <div class="document-title">${documentTitle}</div>
      </div>
      
      <div class="course-info">
        <h3>Course Information</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Course Name:</span> ${
              courseInfo.courseName
            }
          </div>
          <div class="info-item">
            <span class="info-label">Academic Year:</span> ${
              courseInfo.academicYear
            }
          </div>
          <div class="info-item">
            <span class="info-label">Class Name:</span> ${courseInfo.className}
          </div>
          <div class="info-item">
            <span class="info-label">Total Fee Amount:</span> ${formatCurrency(
              totalAmount
            )}
          </div>
        </div>
      </div>
      
      <h3>Fee Types Breakdown</h3>
      <table>
        <thead>
          <tr>
            <th>Fee Type</th>
            <th>Amount</th>
            <th>Due Date</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          ${feeStructures
            .map(
              (structure) => `
            <tr>
              <td>${structure.fee_type}</td>
              <td class="amount">${formatCurrency(structure.amount)}</td>
              <td>${
                structure.due_date
                  ? moment(structure.due_date).format("DD-MM-YYYY")
                  : "-"
              }</td>
              <td>${structure.description || "-"}</td>
            </tr>
          `
            )
            .join("")}
          <tr class="total-row">
            <td>Total</td>
            <td class="amount">${formatCurrency(totalAmount)}</td>
            <td colspan="3"></td>
          </tr>
        </tbody>
      </table>
      
      ${
        includeFooter
          ? `
        <div class="footer">
          <div>Generated on: ${currentDate}</div>
          <div>This is a computer generated document and does not require signature.</div>
        </div>
      `
          : ""
      }
    </body>
    </html>
  `;

  printWindow.document.write(printContent);
  printWindow.document.close();

  // Wait for content to load then print
  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 250);
};

/**
 * Alternative print function for student-specific fee structure
 * @param {Object} studentInfo - Student information object
 * @param {Object} courseInfo - Course information object
 * @param {Array} feeStructures - Array of fee structure objects
 * @param {Object} paymentInfo - Payment information (optional)
 * @param {Object} options - Print options (optional)
 */
export const printStudentFeeStructure = (
  studentInfo,
  courseInfo,
  feeStructures,
  paymentInfo = {},
  options = {}
) => {
  const {
    schoolName = "iSchool Management System",
    documentTitle = `Fee Structure - ${studentInfo.name || "Student"}`,
    customStyles = `
      .student-info {
        background-color: #e3f2fd;
        padding: 15px;
        margin-bottom: 20px;
        border-radius: 5px;
        border: 1px solid #bbdefb;
      }
      .student-info h3 {
        margin-top: 0;
        color: #1976d2;
      }
    `,
  } = options;

  // Add student info section to the print content
  const enhancedCourseInfo = {
    ...courseInfo,
    studentName: studentInfo.name,
    studentId: studentInfo.id,
    rollNumber: studentInfo.rollNumber,
  };

  printFeeStructure(enhancedCourseInfo, feeStructures, {
    schoolName,
    documentTitle,
    customStyles,
  });
};

const printUtils = {
  printFeeStructure,
  printStudentFeeStructure,
  formatCurrency,
  calculateTotalAmount,
};

/**
 * Generates and prints a payment receipt
 * @param {Object} payment - Payment information object
 * @param {Object} studentInfo - Student information object
 * @param {Object} options - Print options (optional)
 */
export const printPaymentReceipt = (
  payment,
  studentInfo = {},
  options = {}
) => {
  const {
    schoolName = "iSchool Management System",
    schoolAddress = "",
    schoolPhone = "",
    schoolEmail = "",
  } = options;

  const printWindow = window.open("", "_blank");
  const currentDate = moment().format("DD-MM-YYYY");
  const paymentDate = payment.payment_date
    ? moment(payment.payment_date).format("DD-MM-YYYY")
    : "";

  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Payment Receipt - ${payment.receipt_number}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: Arial, sans-serif;
          padding: 20px;
          color: #333;
          max-width: 800px;
          margin: 0 auto;
        }
        .receipt-container {
          border: 2px solid #333;
          padding: 20px;
        }
        .header {
          text-align: center;
          border-bottom: 2px solid #333;
          padding-bottom: 15px;
          margin-bottom: 20px;
        }
        .school-name {
          font-size: 24px;
          font-weight: bold;
          color: #1976d2;
          margin-bottom: 5px;
        }
        .school-address {
          font-size: 12px;
          color: #666;
        }
        .receipt-title {
          font-size: 18px;
          font-weight: bold;
          background-color: #1976d2;
          color: white;
          padding: 8px;
          margin: 15px 0;
          text-align: center;
        }
        .receipt-meta {
          display: flex;
          justify-content: space-between;
          margin-bottom: 20px;
          padding: 10px;
          background-color: #f5f5f5;
          border-radius: 5px;
        }
        .meta-item {
          text-align: center;
        }
        .meta-label {
          font-size: 11px;
          color: #666;
          text-transform: uppercase;
        }
        .meta-value {
          font-size: 14px;
          font-weight: bold;
          color: #333;
        }
        .section {
          margin-bottom: 20px;
        }
        .section-title {
          font-size: 14px;
          font-weight: bold;
          color: #1976d2;
          border-bottom: 1px solid #ddd;
          padding-bottom: 5px;
          margin-bottom: 10px;
        }
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .info-item {
          display: flex;
          gap: 10px;
        }
        .info-label {
          font-weight: bold;
          color: #666;
          min-width: 120px;
        }
        .info-value {
          color: #333;
        }
        .payment-details {
          border: 1px solid #ddd;
          border-radius: 5px;
          overflow: hidden;
        }
        .payment-row {
          display: flex;
          justify-content: space-between;
          padding: 12px 15px;
          border-bottom: 1px solid #eee;
        }
        .payment-row:last-child {
          border-bottom: none;
        }
        .payment-row.highlight {
          background-color: #e8f5e9;
          font-weight: bold;
          font-size: 16px;
        }
        .payment-label {
          color: #666;
        }
        .payment-value {
          font-weight: bold;
        }
        .amount-words {
          background-color: #fff3e0;
          padding: 10px 15px;
          border-radius: 5px;
          margin-top: 15px;
          font-style: italic;
        }
        .footer {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px dashed #999;
        }
        .signature-section {
          display: flex;
          justify-content: space-between;
          margin-top: 40px;
        }
        .signature-box {
          text-align: center;
          width: 200px;
        }
        .signature-line {
          border-top: 1px solid #333;
          margin-top: 50px;
          padding-top: 5px;
          font-size: 12px;
        }
        .print-note {
          text-align: center;
          font-size: 11px;
          color: #999;
          margin-top: 20px;
        }
        .watermark {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-45deg);
          font-size: 80px;
          color: rgba(0, 128, 0, 0.1);
          font-weight: bold;
          pointer-events: none;
          z-index: -1;
        }
        @media print {
          body { 
            padding: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print { display: none; }
          .receipt-container {
            border: 2px solid #333;
          }
        }
      </style>
    </head>
    <body>
      <div class="receipt-container" style="position: relative;">
        <div class="watermark">PAID</div>
        
        <div class="header">
          <div class="school-name">${schoolName}</div>
          ${
            schoolAddress
              ? `<div class="school-address">${schoolAddress}</div>`
              : ""
          }
          ${
            schoolPhone || schoolEmail
              ? `<div class="school-address">${[schoolPhone, schoolEmail]
                  .filter(Boolean)
                  .join(" | ")}</div>`
              : ""
          }
        </div>
        
        <div class="receipt-title">PAYMENT RECEIPT</div>
        
        <div class="receipt-meta">
          <div class="meta-item">
            <div class="meta-label">Receipt No.</div>
            <div class="meta-value">${payment.receipt_number || "N/A"}</div>
          </div>
          <div class="meta-item">
            <div class="meta-label">Payment Date</div>
            <div class="meta-value">${paymentDate}</div>
          </div>
          <div class="meta-item">
            <div class="meta-label">Academic Year</div>
            <div class="meta-value">${payment.academic_year || "N/A"}</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Student Information</div>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Student Name:</span>
              <span class="info-value">${
                studentInfo.name ||
                `${payment.first_name || ""} ${
                  payment.last_name || ""
                }`.trim() ||
                "N/A"
              }</span>
            </div>
            <div class="info-item">
              <span class="info-label">Admission No:</span>
              <span class="info-value">${
                studentInfo.admissionNo || payment.admission_no || "N/A"
              }</span>
            </div>
            <div class="info-item">
              <span class="info-label">Course:</span>
              <span class="info-value">${payment.course_name || "N/A"}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Student ID:</span>
              <span class="info-value">${payment.student_id || "N/A"}</span>
            </div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Payment Details</div>
          <div class="payment-details">
            <div class="payment-row">
              <span class="payment-label">Fee Type</span>
              <span class="payment-value">${payment.fee_type || "N/A"}</span>
            </div>
            <div class="payment-row">
              <span class="payment-label">Payment Method</span>
              <span class="payment-value">${
                payment.payment_method || "N/A"
              }</span>
            </div>
            ${
              payment.transaction_reference
                ? `
            <div class="payment-row">
              <span class="payment-label">Transaction Reference</span>
              <span class="payment-value">${payment.transaction_reference}</span>
            </div>
            `
                : ""
            }
            <div class="payment-row highlight">
              <span class="payment-label">Amount Paid</span>
              <span class="payment-value">${formatCurrency(
                payment.amount_paid
              )}</span>
            </div>
          </div>
          
          <div class="amount-words">
            <strong>Amount in words:</strong> ${numberToWords(
              payment.amount_paid
            )} Only
          </div>
        </div>
        
        ${
          payment.notes
            ? `
        <div class="section">
          <div class="section-title">Notes</div>
          <p>${payment.notes}</p>
        </div>
        `
            : ""
        }
        
        <div class="footer">
          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line">Student/Parent Signature</div>
            </div>
            <div class="signature-box">
              <div class="signature-line">Authorized Signature</div>
            </div>
          </div>
          
          <div class="print-note">
            <p>This is a computer generated receipt.</p>
            <p>Generated on: ${currentDate}</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(printContent);
  printWindow.document.close();

  // Wait for content to load then print
  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 250);
};

/**
 * Converts a number to words (Indian numbering system)
 * @param {number} num - The number to convert
 * @returns {string} Number in words
 */
const numberToWords = (num) => {
  if (num === 0) return "Zero";

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const convertLessThanThousand = (n) => {
    if (n === 0) return "";
    if (n < 20) return ones[n];
    if (n < 100)
      return (
        tens[Math.floor(n / 10)] + (n % 10 === 0 ? "" : " " + ones[n % 10])
      );
    return (
      ones[Math.floor(n / 100)] +
      " Hundred" +
      (n % 100 === 0 ? "" : " " + convertLessThanThousand(n % 100))
    );
  };

  const number = Math.floor(Math.abs(num));

  if (number < 1000) return convertLessThanThousand(number) + " Rupees";
  if (number < 100000) {
    return (
      convertLessThanThousand(Math.floor(number / 1000)) +
      " Thousand" +
      (number % 1000 === 0
        ? ""
        : " " + convertLessThanThousand(number % 1000)) +
      " Rupees"
    );
  }
  if (number < 10000000) {
    return (
      convertLessThanThousand(Math.floor(number / 100000)) +
      " Lakh" +
      (number % 100000 === 0
        ? ""
        : " " + numberToWords(number % 100000).replace(" Rupees", "")) +
      " Rupees"
    );
  }
  return (
    convertLessThanThousand(Math.floor(number / 10000000)) +
    " Crore" +
    (number % 10000000 === 0
      ? ""
      : " " + numberToWords(number % 10000000).replace(" Rupees", "")) +
    " Rupees"
  );
};

export default printUtils;
